import { Component, computed, effect, inject, input, OnInit, Optional, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BaseComponent } from 'src/app/shared/components/base.component';
import { PaymentInformationModel } from 'src/app/interfaces/payment-information';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PaymentService } from '../services/payment.service';
import { finalize } from 'rxjs/operators';
import { PaymentSetupRequestModel } from 'src/app/interfaces/payment-integration';
import { ApplicableFor, PaymentFlowType } from 'src/app/interfaces/payment-enums';
import { AppointmentRequestModel } from '../models/request/appointment-request';
import { WebStorageService } from '../services/web-storage.service';
import { PaymentSetupModel } from 'src/app/interfaces/payment-setup';
import { PaymentResponseModel } from 'src/app/interfaces/payment-response';
import { PaymentResponseCodes } from 'src/app/interfaces/payment-response-code';

@Component({
  selector: 'app-collect-payment',
  templateUrl: './collect-payment.component.html',
  styleUrls: ['./collect-payment.component.sass'],
  providers: [DialogService],
  standalone: false
})
export class CollectPaymentComponent extends BaseComponent implements OnInit {
  salesOrderId = input<number>();
  flowType = input<PaymentFlowType>();
  applicableFor = input<ApplicableFor>();
  appointmentRequest = input<AppointmentRequestModel>();
  paymentFlowType = PaymentFlowType;
  paymentData = signal<PaymentInformationModel | null>(null);
  paymentSetup: PaymentSetupModel;
  hostedPaymentUrl: SafeResourceUrl;
  selectedPaymentMethod: 'existing' | 'new' | null = null;
  private sanitizer = inject(DomSanitizer);
  private boundMessageHandler = (event: MessageEvent) => this.onHostedReturnMessage(event);
  paymentCompleted: boolean = false;
  buttonText = 'Collect Payment';
  paymentResponse: PaymentResponseModel;
  message: { title: string; body: string } | null = null;
  showStartOverButton = false;
  isReplaceCard = false;
  private salesOrderIdMerged = computed(() => {
    const dialogValue = this.dialogConfig?.data?.salesOrderId;
    const inputValue = this.salesOrderId();
    return dialogValue || inputValue || null;
  });

  bookAppointmentMerged = computed(() => {
    const dialogValue = this.dialogConfig?.data?.bookAppointment;
    if (dialogValue !== undefined && dialogValue !== null) {
      return dialogValue;
    }
    return null;
  });

  flowTypeMerged = computed(() => {
    const dialogValue = this.dialogConfig?.data?.flowType;
    if (dialogValue !== undefined && dialogValue !== null) {
      return dialogValue;
    }
    return this.flowType();
  });

  appointmentRequestModelMerged = computed(() => {
    const dialogValue = this.dialogConfig?.data?.appointmentObj;
    if (dialogValue !== undefined && dialogValue !== null) {
      return dialogValue;
    }
    return this.appointmentRequest();
  });

  appointmentCancelRequestModelMerged = computed(() => {
    const dialogValue = this.dialogConfig?.data?.appointmentCancelRequest;
    if (dialogValue !== undefined && dialogValue !== null) {
      return dialogValue;
    }
  });

  dialogTitle = computed(() => {
    if (this.flowTypeMerged() === PaymentFlowType.CancelFees && this.applicableForMerged() === ApplicableFor.Patient) {
      return 'Pay Cancellation Fee';
    }

    const flowType = this.flowTypeMerged();
    switch (flowType) {
      case PaymentFlowType.SaveOnly:
        return 'Add Credit Card';
      case PaymentFlowType.PayOnly:
        return 'Collect Payment';
      case PaymentFlowType.PayAndSave:
        return 'Add Card & Collect Payment';
      default:
        return 'Payment Processing';
    }
  });

  private applicableForMerged = computed(() => {
    const dialogValue = this.dialogConfig?.data?.applicableFor;
    if (dialogValue !== undefined && dialogValue !== null) {
      return dialogValue;
    }
    return this.applicableFor();
  });

  constructor(
    messageService: MessageService,
    private paymentService: PaymentService,
    private webStorageService: WebStorageService,
    @Optional() private dialogRef: DynamicDialogRef,
    @Optional() public dialogConfig: DynamicDialogConfig
  ) {
    super(messageService);

    effect(() => {
      const salesOrderId = this.salesOrderIdMerged();
      if (salesOrderId !== undefined && salesOrderId !== null && salesOrderId > 0) {
        this.getPaymentInformation();
      }
    });

    window.addEventListener('message', this.boundMessageHandler);
  }

  ngOnInit(): void {
    this.showStartOverButton = false;
    if (this.applicableForMerged() === ApplicableFor.Patient) {
      this.buttonText = this.webStorageService.getPaymentStepName();
    }

    if (this.flowTypeMerged() === PaymentFlowType.CancelFees && this.applicableForMerged() === ApplicableFor.Patient) {
      this.buttonText = 'Pay Cancellation Fee';
    }
  }

  chargeCard(): void {
    if (this.flowTypeMerged() === PaymentFlowType.CancelFees && this.applicableForMerged() === ApplicableFor.Patient) {
      this.chargeCancellation();
    } else {
      this.chargeRegularPayment();
    }
  }

  private chargeCancellation(): void {
    const requestData: PaymentSetupRequestModel = {
      ...this.dialogConfig?.data
    };
    this.loadingCustom = true;
    this.paymentService
      .chargeCancellation(requestData)
      .pipe(finalize(() => (this.loadingCustom = false)))
      .subscribe((result) => {
        this.processPaymentResponse(result.responseModel);
      });
  }

  private chargeRegularPayment(): void {
    const flowType = this.flowTypeMerged();
    const applicableFor = this.applicableForMerged();
    const salesOrderId = this.salesOrderIdMerged();
    const appointmentRequestModel = this.appointmentRequestModelMerged();

    const requestData: PaymentSetupRequestModel = {
      salesOrderId: salesOrderId,
      flowType: flowType,
      applicableFor: applicableFor,
      appointmentRequest: appointmentRequestModel
    };

    this.loadingCustom = true;
    this.paymentService
      .chargeAndBook(requestData)
      .pipe(finalize(() => (this.loadingCustom = false)))
      .subscribe((result) => {
        this.processPaymentResponse(result.responseModel);
        //this.dialogRef?.close({ success: true, appointmentId: result.responseModel?.appointmentId });
      });
  }

  private async onHostedReturnMessage(event: MessageEvent) {
    if (!event.data || event.data.type !== 'WORLDPAY_HOSTED_RETURN') {
      return;
    }

    if (!event.data.payload) {
      return;
    }

    this.paymentResponse = this.mapWorldPayResponse(event.data.payload);
    this.processPaymentResponse(this.paymentResponse);
  }

  cancelDialog() {
    this.paymentData.set(null);
    this.dialogRef?.close({ success: false });
  }

  loadPaymentSetup() {
    this.paymentCompleted = false;
    this.paymentSetup = null;
    this.webStorageService.deletePaymentSetup();
    this.selectedPaymentMethod = 'new';
    const flowType = this.flowTypeMerged();
    const applicableFor = this.applicableForMerged();
    const salesOrderId = this.salesOrderIdMerged();
    const appointmentRequestModel = this.appointmentRequestModelMerged();
    const cancelRequestModel = this.appointmentCancelRequestModelMerged();
    if (this.paymentData()?.paymentCard != null) {
      this.isReplaceCard = true;
    }
    const requestData: PaymentSetupRequestModel = {
      salesOrderId: salesOrderId,
      flowType: flowType,
      applicableFor: applicableFor,
      appointmentRequest: appointmentRequestModel,
      appointmentCancelRequest: cancelRequestModel
    };

    this.loading = true;
    this.paymentService
      .createPaymentSetup(requestData)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((result) => {
        this.paymentSetup = result.responseModel;
        this.webStorageService.setPaymentSetup(this.paymentSetup);
        this.hostedPaymentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.paymentSetup.hostedUrl);
      });
  }

  switchToExistingCard() {
    this.isReplaceCard = false;
    if (this.paymentSetup) {
      this.paymentService.clearPaymentSetup(this.paymentSetup).subscribe(() => {
        this.paymentSetup = null;
        this.webStorageService.deletePaymentSetup();
        this.selectedPaymentMethod = 'existing';
      });
    } else {
      this.selectedPaymentMethod = 'existing';
    }
  }

  getPaymentInformation() {
    const salesOrderId = this.salesOrderIdMerged();
    this.paymentService.getPaymentInformation(salesOrderId).subscribe((result) => {
      this.paymentData.set(result.responseModel);
      if (this.paymentData().paymentCard != null) {
        this.selectedPaymentMethod = 'existing';
      } else {
        this.selectedPaymentMethod = 'new';
        this.loadPaymentSetup();
      }
    });
  }

  onTimeoutExpired() {
    this.showStartOverButton = true;
    if (!this.paymentSetup) {
      return;
    }

    this.paymentService
      .timeoutPaymentSetup(this.paymentSetup)
      .pipe(
        finalize(() => {
          this.loadingCustom = false;
        })
      )
      .subscribe((result) => {
        this.processPaymentResponse(result.responseModel);
      });
  }

  private processPaymentResponse(result: PaymentResponseModel) {
    this.paymentResponse = result;
    this.getResponseMessage(this.paymentResponse, this.flowTypeMerged(), this.bookAppointmentMerged());
    this.paymentCompleted = true;
    this.loadingCustom = false;
    this.paymentService.paymentCompleted$.next(this.paymentResponse);
    this.paymentService.paymentCompletedPatient$.next(this.paymentResponse);
  }

  private mapWorldPayResponse(worldPayResponse: any): PaymentResponseModel {
    return {
      isSuccess: worldPayResponse.IsSuccess,
      message: worldPayResponse.Message,
      code: worldPayResponse.Code,
      appointmentId: worldPayResponse.AppointmentId,
      chargedAmount: worldPayResponse.ChargedAmount,
      transactionID: worldPayResponse.TransactionID,
      paymentFlowType: worldPayResponse.PaymentFlowType,
      hasAppointmentError: worldPayResponse.HasAppointmentError,
      appointmentError: worldPayResponse.AppointmentError
    };
  }

  private getResponseMessage(response: PaymentResponseModel, flowType: PaymentFlowType, bookAppointment: boolean) {
    if (response.code === PaymentResponseCodes.ApprovedSuccess) {
      this.showStartOverButton = false;
      switch (flowType) {
        case PaymentFlowType.SaveOnly:
          if (bookAppointment) {
            this.dialogRef?.close({ success: true, response: response });
          }
          this.message = { title: 'Card saved successfully!', body: '' };
          break;

        case PaymentFlowType.PayOnly:
          if (bookAppointment) {
            this.dialogRef?.close({ success: true, response: response });
          }
          this.message = { title: 'Payment Successful', body: `` }; //Payment Successful! Amount: ${response.chargedAmount} & Transaction ID: ${response.transactionID};
          break;

        case PaymentFlowType.PayAndSave:
          if (bookAppointment) {
            this.dialogRef?.close({ success: true, response: response });
          }
          this.message = { title: 'Card Saved & Payment Successful!', body: `` }; //Card Saved & Payment Successful! Amount: ${response.chargedAmount} & Transaction ID: ${response.transactionID};
          break;

        case PaymentFlowType.CancelFees:
          this.dialogRef?.close({ success: true, ...response });
          this.message = { title: 'Payment Successful', body: `` }; //Payment Successful! Amount: ${response.chargedAmount} & Transaction ID: ${response.transactionID}
          break;

        default:
          this.message = { title: 'Transaction Successful', body: 'Operation completed successfully!' };
      }
    } else if (response.code === PaymentResponseCodes.NotDefined || response.code === PaymentResponseCodes.InvalidData) {
      switch (flowType) {
        case PaymentFlowType.PayOnly:
          this.message = { title: 'Payment Failed', body: 'Session Expired! Please try again after sometime upon reviewing Payment Status.' };
          break;

        default:
          this.message = { title: 'Payment Failed', body: 'Session Expired! Please try again after sometime.' };
      }
    } else if (response.code === PaymentResponseCodes.CardTokenizationFailed) {
      switch (flowType) {
        case PaymentFlowType.PayAndSave:
          if (bookAppointment) {
            this.dialogRef?.close({ success: true, response: response });
          }
          this.message = { title: 'Payment Successful', body: `Card Not Saved ${response.message}` };
          break;
        default:
          this.message = { title: 'Payment Failed', body: 'Session Expired! Please try again after sometime.' };
      }
    }
    else if (response.code === PaymentResponseCodes.Declined) {
      this.message = {
        title: 'Payment Declined', body: 'Payment Declined. Please verify the transaction status in Payment History before retrying.'
      };
    }
    else {
      if (flowType === PaymentFlowType.PayOnly) {
        if (response.code === PaymentResponseCodes.CommunicationError || response.code === PaymentResponseCodes.HostError) {
          this.message = {
            title: 'Payment Failed',
            body: 'Unable to reach the payment processor. Please verify the transaction status in Payment History before retrying.'
          };
        }
      }
      else {
        this.message = { title: 'Payment Failed', body: response.message };
      }
    }
  }

  closePaymentResult(): void {
    this.paymentCompleted = false;
    if (this.paymentResponse?.isSuccess) {
      if (this.dialogRef) {
        this.dialogRef.close({ success: true, response: this.paymentResponse });
      }
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.boundMessageHandler);
  }
}
